/**
 * Street Dog Co. — Dynamic Collection Pin
 * ----------------------------------------
 * Pins a specific product to the first slot of the collection grid when
 * the page URL includes a `first` parameter (product handle, or a full
 * product URL like Facebook's catalog deep links send).
 *
 * URL params:
 *   ?first=<handle-or-product-url>   (required to activate)
 *
 * Behaviour:
 *  1. If the product is already rendered in the grid, move it to slot #1.
 *     No network request — instant, no flicker.
 *  2. If it's NOT already rendered (large collections, product ranked
 *     below the page size), fetch its data via Shopify's built-in
 *     /products/{handle}.js endpoint, CLONE an existing real product
 *     card (so it uses the theme's actual <product-card> custom element
 *     and CSS — not hand-built markup that can render broken/blank),
 *     swap in the new product's content, and prepend it.
 */
(() => {
  function getHandleFromParam(raw) {
    if (!raw) return null;
    try {
      if (raw.indexOf('http') === 0 || raw.indexOf('%2F') !== -1 || raw.indexOf('/products/') !== -1) {
        const decoded = decodeURIComponent(raw);
        const match = decoded.match(/\/products\/([a-zA-Z0-9\-_.]+)/);
        if (match) return match[1];
      }
    } catch (e) {
      /* fall through to treating raw as a plain handle */
    }
    return raw;
  }

  function moneyFormat(cents) {
    try {
      const currency =
        (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
      return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(cents / 100);
    } catch (e) {
      return '$' + (cents / 100).toFixed(2);
    }
  }

  async function fetchProductJSON(handle) {
    const res = await fetch(`/products/${handle}.js`);
    if (!res.ok) throw new Error('Product fetch failed: ' + res.status);
    return res.json();
  }

  function buildClonedCard(templateLi, product) {
    // Capture the template's original visible text BEFORE cloning, so we
    // know exactly which text nodes to swap inside the clone.
    const originalTitleEl = templateLi.querySelector('a.product-card__link .visually-hidden');
    const originalTitleText = originalTitleEl ? originalTitleEl.textContent.trim() : null;

    // Grab all distinct "$X.XX"-style price strings currently in the card
    // (covers both the sale price and the struck-through compare-at price).
    const priceRegex = /\$[\d,]+\.\d{2}/g;
    const originalPrices = Array.from(new Set((templateLi.textContent.match(priceRegex) || [])));

    const clone = templateLi.cloneNode(true);
    clone.removeAttribute('id');
    clone.setAttribute('data-product-id', product.id);
    clone.setAttribute('data-pinned-fallback', 'true');
    clone.className = 'product-grid__item product-grid__item--0';

    // Swap every link that points at the old product to point at the new one.
    clone.querySelectorAll('a[href]').forEach((a) => {
      a.setAttribute('href', product.url);
    });

    // Swap image(s).
    const newImage = product.featured_image || (product.images && product.images[0]) || '';
    if (newImage) {
      clone.querySelectorAll('img').forEach((img) => {
        img.setAttribute('src', newImage);
        img.removeAttribute('srcset');
        img.setAttribute('alt', product.title);
      });
    }

    // Swap title text (both the visually-hidden span and the visible title
    // block use the exact same string, so one pass catches both).
    if (originalTitleText) {
      walkTextNodes(clone, (node) => {
        if (node.textContent.trim() === originalTitleText) {
          node.textContent = node.textContent.replace(originalTitleText, product.title);
        }
      });
    }

    // Swap price text. New card: sale price + optional compare-at price.
    const newPrice = moneyFormat(product.price);
    const newCompareAt =
      product.compare_at_price && product.compare_at_price > product.price
        ? moneyFormat(product.compare_at_price)
        : null;

    originalPrices.forEach((oldPriceStr, i) => {
      const replacement = i === 0 ? newPrice : newCompareAt || newPrice;
      walkTextNodes(clone, (node) => {
        if (node.textContent.includes(oldPriceStr)) {
          node.textContent = node.textContent.replace(oldPriceStr, replacement);
        }
      });
    });

    return clone;
  }

  function walkTextNodes(root, callback) {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
    let node;
    // Collect first (mutating while walking can skip nodes).
    const nodes = [];
    while ((node = walker.nextNode())) nodes.push(node);
    nodes.forEach(callback);
  }

  async function pinFirstProduct() {
    const params = new URLSearchParams(window.location.search);
    const rawFirst = params.get('first');
    if (!rawFirst) return;

    const handle = getHandleFromParam(rawFirst);
    if (!handle) return;

    const grid = document.querySelector('ul.product-grid[ref="grid"]') || document.querySelector('ul.product-grid');
    if (!grid) return;

    const items = Array.from(grid.children).filter((el) => el.tagName === 'LI');
    if (items.length === 0) return;

    // --- Fast path: product already rendered in this page's grid ---
    const existing = items.find((li) => {
      const link = li.querySelector('a.product-card__link, a[href*="/products/"]');
      return link && link.getAttribute('href') && link.getAttribute('href').indexOf(`/products/${handle}`) !== -1;
    });

    if (existing) {
      if (grid.firstElementChild !== existing) {
        grid.prepend(existing);
      }
      return;
    }

    // --- Fallback path: fetch product data, clone a real card, swap content ---
    try {
      const product = await fetchProductJSON(handle);
      const templateLi = items[0];
      const card = buildClonedCard(templateLi, product);
      grid.prepend(card);
    } catch (err) {
      console.warn('[pin-first-product] could not pin product:', handle, err);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', pinFirstProduct);
  } else {
    pinFirstProduct();
  }
})();
